/* SESH attribution: content -> product/cart/order and SIA */
(() => {
  'use strict';
  if (window.__seshAttributionV1) return;
  window.__seshAttributionV1 = true;
  const STORAGE_KEY = 'sesh:attribution:v1';
  const TTL = 1800000; // 30-minute inactivity window
  const PAGE = location.pathname.replace(/\/+$/, '') || '/';
  const cleanPath = path => String(path || '').split('?')[0].slice(0, 180);
  const contentType = p => {
    if (/\/(?:ar\/)?blogs\//.test(p)) return 'blog';
    if (/\/(?:ar\/)?pages\/sesh-academy(?:-|$)/.test(p)) return 'academy';
    if (/\/(?:ar\/)?pages\/(?:ingredient(?:s-library)?)(?:-|$)/.test(p)) return 'ingredient';
    return '';
  };
  const isProduct = p => /\/products\//.test(p);
  const privacyOK = () => {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed() === true;
    } catch (_) { return false; }
  };
  const read = () => {
    try {
      const s = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return s && Date.now() - (s.lastSeen || s.started || 0) < TTL ? s : { started: Date.now() };
    } catch (_) { return { started: Date.now() }; }
  };
  let state = read();
  state.lastSeen = Date.now();
  const save = () => {
    if (!privacyOK()) return;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  };
  const send = (name, props = {}) => {
    if (!privacyOK()) return;
    const params = { ...props, tracking_version: 'sesh_v1' };
    try { if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, params); } catch (_) {}
    // Publish once to Shopify Customer Events. A connected pixel must forward to GA4.
    // Do not call gtag/dataLayer here: that could duplicate GA4 events.
  };
  const cartAttrs = () => ({
    'SESH attribution - content type': cleanPath(state.lastContent?.type),
    'SESH attribution - content page': cleanPath(state.lastContent?.path),
    'SESH attribution - first content': cleanPath(state.firstContent?.path),
    'SESH attribution - product page': cleanPath(state.productPath),
    'SESH attribution - SIA asked': state.siaAsked ? 'yes' : 'no',
    'SESH attribution - SIA recommendation': state.siaRecommended ? 'yes' : 'no'
  });
  const syncCart = async () => {
    if (!privacyOK() || !(state.lastContent || state.siaAsked)) return;
    const attributes = cartAttrs(), signature = JSON.stringify(attributes);
    if (state.lastCartSync === signature || window.__seshCartSyncRunning) return;
    window.__seshCartSyncRunning = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      });
      if (response.ok) { state.lastCartSync = signature; save(); }
    } catch (_) {}
    finally {
      window.__seshCartSyncRunning = false;
      // Source can change while Shopify is processing the update.
      if (privacyOK() && JSON.stringify(cartAttrs()) !== signature) {
        setTimeout(syncCart, 0);
      }
    }
  };
  const currentType = contentType(PAGE);
  let contentPageSent = false;
  let productPageSent = false;
  const initializePage = () => {
  if (currentType && privacyOK() && !contentPageSent) {
    contentPageSent = true;
    state.lastContent = { type: currentType, path: cleanPath(PAGE), at: Date.now() };
    if (!state.firstContent) state.firstContent = state.lastContent;
    save();
    send('sesh_content_view', { source_type: currentType, source_path: PAGE });
  }
  if (isProduct(PAGE) && privacyOK() && !productPageSent) {
    productPageSent = true;
    const p = state.pending;
    const direct = p && p.productPath === PAGE && Date.now() - p.at < 1800000;
    if (direct) {
      state.productPath = PAGE;
      if (p.from === 'sia') state.siaRecommended = true;
      if (p.content) state.lastContent = p.content;
      send('sesh_product_referral_view', {
        source_type: p.from, source_path: p.content?.path || '(sia)',
        product_path: PAGE
      });
      state.pending = null;
      save();
      syncCart();
    } else if (state.lastContent && Date.now() - state.lastContent.at < TTL) {
      state.productPath = PAGE;
      send('sesh_content_assisted_product_view', {
        source_type: state.lastContent.type,
        source_path: state.lastContent.path,
        product_path: PAGE
      });
      save();
      syncCart();
    }
  }
  };
  // Customer Privacy API may load asynchronously, and tracking must wait for it.
  try {
    if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], err => {
        if (!err) initializePage();
      });
    } else {
      initializePage(); // May already be loaded by the storefront.
    }
  } catch (_) {}
  document.addEventListener('visitorConsentCollected', initializePage);
  // Track SIA when the conversation dialog really opens. Some entry points
  // bypass the intelligence event, so keep both DOM and internal-event signals.
  let lastSiaOpenAt = 0;
  const markSiaOpen = route => {
    if (!privacyOK()) return;
    const now = Date.now();
    if (now - lastSiaOpenAt < 1500) return; // Do not double count one opening.
    lastSiaOpenAt = now;
    send('sesh_sia_open', {
      route: String(route || 'default').slice(0, 40), page_path: PAGE
    });
  };
  document.addEventListener('sesh:intelligence', e => {
    if (e.detail?.sesh_event === 'copilot_open') markSiaOpen(e.detail?.route);
  });
  const watchSiaDialog = () => {
    const dialog = document.querySelector('[data-sesh-copilot-dialog]');
    if (!dialog || typeof MutationObserver !== 'function') return;
    let wasOpen = dialog.hasAttribute('open');
    const observer = new MutationObserver(() => {
      const nowOpen = dialog.hasAttribute('open');
      if (nowOpen && !wasOpen) markSiaOpen('dialog');
      wasOpen = nowOpen;
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchSiaDialog, { once: true });
  } else {
    watchSiaDialog();
  }
  let lastQuestionAt = 0;
  const markQuestion = () => {
    if (!privacyOK()) return;
    const input = document.querySelector('[data-sesh-chat-input]');
    if (!input?.value.trim() || input.disabled || Date.now() - lastQuestionAt < 1000) return;
    lastQuestionAt = Date.now();
    const first = !state.siaAsked;
    state.siaAsked = true;
    state.siaQuestions = (state.siaQuestions || 0) + 1;
    save();
    send('sesh_sia_question_submit', { page_path: PAGE });
    if (first) {
      send('sesh_sia_active_session', { page_path: PAGE });
      syncCart();
    }
  };
  document.addEventListener('click', e => {
    if (!(e.target instanceof Element)) return;
    // Capture launcher clicks as a fallback when the dialog event is missed.
    const launch = e.target.closest('[data-sesh-open-copilot]');
    if (launch) markSiaOpen(launch.dataset.seshSiaRoute || 'default');
    if (e.target.closest('[data-sesh-chat-send]')) markQuestion();
    if (e.target.closest('[data-sesh-chat-add]')) {
      state.siaRecommended = true;
      save();
      send('sesh_sia_add_intent', { page_path: PAGE });
      syncCart();
    }
    const a = e.target.closest('a[href]');
    if (!a) return;
    let url;
    try { url = new URL(a.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin || !isProduct(url.pathname)) return;
    const path = cleanPath(url.pathname);
    const fromSia = !!a.closest('[data-sesh-chat-log], [data-sesh-concierge]');
    if (!currentType && !fromSia) return;
    const from = fromSia ? 'sia' : currentType;
    const content = currentType ?
      { type: currentType, path: PAGE, at: Date.now() } : state.lastContent;
    state.pending = { from, content, productPath: path, at: Date.now() };
    if (fromSia) state.siaRecommended = true;
    save();
    send(fromSia ? 'sesh_sia_product_click' : 'sesh_content_product_click', {
      source_type: from, source_path: content?.path || '(sia)', product_path: path
    });
  }, true);
  // Cart attributes are synced after cart changes to cover quick-add and content cards.
  let syncTimer;
  const afterCartChange = () => {
    clearTimeout(syncTimer);
    state.lastCartSync = null; // Shopify cart may be rebuilt after an add/refresh.
    syncTimer = setTimeout(syncCart, 450);
  };
  ['cart-updated', 'cart:updated', 'cart:refresh', 'sesh:cart-updated'].forEach(name => {
    document.addEventListener(name, afterCartChange);
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form?.matches?.('form[action*="/cart/add"]')) afterCartChange();
  }, true);
  document.addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey &&
        e.target?.matches?.('[data-sesh-chat-input]')) markQuestion();
  }, true);
})();